import * as React from "react";
import {useContext, useState, useRef, useEffect} from "react";
import {StoreContext} from "../../stores/StoreLoader";
import {GridTypes, IBreadcrumb} from "../../pages/MainGridPage";
import styles from "./styles/TerraceSubPageHeader.module.scss"
import ClickableLink from "../utilities/ClickableLink";
import FontAwesome from "../utilities/FontAwesome";
import {EditableTitle} from "./Breadcrumbs";
import FollowPageButton from "./FollowPageButton";
import WatsonApi from "../../backends/WatsonApi";
import NotificationManager from "../notifications/NotificationManager";
import {IMicrositeLink} from "../header/microsite/Microsite";
import classNames from 'classnames';
import {observer} from "mobx-react";
import dynamic from "next/dynamic";

const DraggableSubPageNavigation = dynamic(() => import("./TerraceSubPageNavigation"));

const TerraceSubPageHeader: React.FC<{
    breadcrumbs: IBreadcrumb[],
    editable?: boolean,
}> = observer(props => {
    const {organizationStore, modalStore, userStore, interfaceStore} = useContext(StoreContext);
    const [reorderingEnabled, setReorderingEnabled] = useState(false);
    let {breadcrumbs} = props;

    const micrositeContainerOverlayRightClassName = classNames({
        [styles.micrositeContainerOverlayRight]: true,
    })

    if (interfaceStore.breakpoint === "break-point-xs") {
        if (breadcrumbs.length > 1) {
            breadcrumbs = [breadcrumbs[0], breadcrumbs[breadcrumbs.length-1]]
        }
        else {
            breadcrumbs = [breadcrumbs[0]]
        }
    }

    const children: IMicrositeLink[] = organizationStore.currentOrganization.sub_navigations || [];

    const toggleFollowing = async (breadcrumb) => {
        try {
            const client = await WatsonApi();
            await client.apis.toggle_follow.toggle_follow_toggle_follow_organization({
                "itemname": (breadcrumb.model.id),
            });
            breadcrumb.follower = !breadcrumb.follower
            if (breadcrumb.follower) {
                NotificationManager.success(`You are now following ${breadcrumb.label}`)
            } else {
                NotificationManager.success(`You are no longer following ${breadcrumb.label}`)
            }
        } catch (e) {
            console.error(e);
            NotificationManager.error("Unable to follow.\nError: " + e);
        }
    }

    const lastBreadcrumbIndex = breadcrumbs.length - 1;

    return (
        <div className={styles.container}>
            <div>
                {breadcrumbs.map((breadcrumb, index) => {
                    const itemKey = `${organizationStore.currentOrganization.id}-${breadcrumb?.model?.id || breadcrumb.url}`;
                    const breadcrumbsClassName = classNames({
                        ['sb-organization-color-element-font']: true,
                        [styles.breadcrumbSelected]: organizationStore.currentOrganization.id === breadcrumb.model?.id,
                    })
                    return <span key={itemKey} className={styles.title}>
                    <FollowPageButton follower={breadcrumb.follower}
                                      toggleFollowing={() => toggleFollowing(breadcrumb)}
                                      symbolOnly={true}
                                      className={styles.followButton}
                                      size={'2x'}/>&nbsp;
                        {organizationStore.currentOrganization.id === breadcrumb.model?.id ?
                            <div className={breadcrumbsClassName}>
                                <EditableTitle breadcrumb={breadcrumb} editable={props.editable}/>
                            </div>
                            :
                            <ClickableLink href={breadcrumb.url} className={breadcrumbsClassName}>
                                {(interfaceStore.breakpoint === "break-point-xs" &&
                                    organizationStore.organization.id !== organizationStore.currentOrganization.id) ?
                                    <FontAwesome name={'fa-home'} prefix={'fas'}/> :
                                    <>{breadcrumb.label}</>}
                            </ClickableLink>
                        }

                        {index !== lastBreadcrumbIndex &&
                            <FontAwesome name={'fa-chevron-right'} prefix={'fas'}/>}
                </span>
                })}
            </div>
            {(children.length > 0 || userStore.editor) &&
                <div>
                    <div className={styles.micrositeContainer}>
                        {children.map((child, idx) => {
                            if (userStore.editor) {
                                return <DraggableSubPageNavigation reorder={reorderingEnabled} link={child} index={idx}/>
                            } else {
                                return <ClickableLink href={child.url} className={styles.microsite}>
                                    {child.label}
                                </ClickableLink>
                            }
                        })}
                        {userStore.editor && <>
                            {children.length > 0 &&
                                <button title={"Reorder Sub-sections"} onClick={() => setReorderingEnabled(v => !v)}>
                                    <FontAwesome prefix={'fa-solid'} name={'fa-arrows-left-right'} style={{
                                        borderRadius: 5,
                                        border: `3px solid ${reorderingEnabled ? "#545454" : "transparent"}`,
                                    }}/>
                                </button>}
                            <button title={"Add New Sub-section"} onClick={() => {
                                modalStore.addModal({
                                    id: "addOrEditBlock",
                                    type: 'addOrEditBlock',
                                    intent: "add",
                                    blockType: "section",
                                })
                            }}>
                                <FontAwesome prefix={'fa-solid'} name={'fa-plus'}/>
                            </button>
                        </>}
                    </div>
                    <div className={micrositeContainerOverlayRightClassName}></div>
                </div>
            }
        </div>
    )
})

export default TerraceSubPageHeader;
